import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'error' | 'invalid'

  const t = (key) => {
    const texts = {
      title: { fr: 'Nouveau mot de passe', en: 'New password', es: 'Nueva contraseña', de: 'Neues Passwort', pt: 'Nova senha' },
      new_password: { fr: 'Nouveau mot de passe', en: 'New password', es: 'Nueva contraseña', de: 'Neues Passwort', pt: 'Nova senha' },
      confirm: { fr: 'Confirmer le mot de passe', en: 'Confirm password', es: 'Confirmar contraseña', de: 'Passwort bestätigen', pt: 'Confirmar senha' },
      submit: { fr: 'Réinitialiser', en: 'Reset', es: 'Restablecer', de: 'Zurücksetzen', pt: 'Redefinir' },
      success_title: { fr: 'Mot de passe modifié !', en: 'Password changed!', es: '¡Contraseña cambiada!', de: 'Passwort geändert!', pt: 'Senha alterada!' },
      success_text: { fr: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.', en: 'You can now log in with your new password.', es: 'Ahora puedes iniciar sesión con tu nueva contraseña.', de: 'Sie können sich jetzt mit Ihrem neuen Passwort anmelden.', pt: 'Agora você pode entrar com sua nova senha.' },
      invalid_title: { fr: 'Lien invalide', en: 'Invalid link', es: 'Enlace inválido', de: 'Ungültiger Link', pt: 'Link inválido' },
      invalid_text: { fr: 'Ce lien de réinitialisation est invalide ou a expiré.', en: 'This reset link is invalid or has expired.', es: 'Este enlace de restablecimiento es inválido o ha expirado.', de: 'Dieser Link ist ungültig oder abgelaufen.', pt: 'Este link de redefinição é inválido ou expirou.' },
      mismatch: { fr: 'Les mots de passe ne correspondent pas', en: 'Passwords do not match', es: 'Las contraseñas no coinciden', de: 'Passwörter stimmen nicht überein', pt: 'As senhas não coincidem' },
      too_short: { fr: 'Le mot de passe doit contenir au moins 6 caractères', en: 'Password must be at least 6 characters', es: 'La contraseña debe tener al menos 6 caracteres', de: 'Passwort muss mindestens 6 Zeichen lang sein', pt: 'A senha deve ter pelo menos 6 caracteres' },
      back_login: { fr: 'Retour à la connexion', en: 'Back to login', es: 'Volver al inicio de sesión', de: 'Zurück zur Anmeldung', pt: 'Voltar ao login' },
    };
    return texts[key]?.[language] || texts[key]?.en || key;
  };

  useEffect(() => {
    if (!token) setStatus('invalid');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('too_short')); return; }
    if (password !== confirmPassword) { toast.error(t('mismatch')); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setStatus('success');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Invalid or expired token') {
        setStatus('invalid');
      } else {
        toast.error(detail || 'Error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-velvet-bg flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-velvet-bg via-velvet-bg to-purple-dark/20" />
      <div className="absolute top-1/4 -left-32 w-64 h-64 bg-purple-main/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-fuchsia-main/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent mb-8 transition-colors">
          <ArrowLeft size={20} />
          {t('back_login')}
        </Link>
        
        <div className="glass rounded-2xl p-8 animate-slideUp">
          {status === 'success' ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <h2 className="font-heading text-2xl text-velvet_text-primary mb-3">{t('success_title')}</h2>
              <p className="text-velvet_text-secondary mb-6">{t('success_text')}</p>
              <Link to="/login">
                <Button className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12" data-testid="back-to-login-button">
                  {t('back_login')}
                </Button>
              </Link>
            </div>
          ) : status === 'invalid' ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle size={32} className="text-red-400" />
              </div>
              <h2 className="font-heading text-2xl text-velvet_text-primary mb-3">{t('invalid_title')}</h2>
              <p className="text-velvet_text-secondary mb-6">{t('invalid_text')}</p>
              <Link to="/forgot-password">
                <Button className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12">
                  {language === 'fr' ? 'Réessayer' : 'Try again'}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main flex items-center justify-center">
                <Lock size={28} className="text-white" />
              </div>
              <h1 className="font-heading text-3xl text-velvet_text-primary mb-6 text-center">{t('title')}</h1>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-velvet_text-secondary">{t('new_password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12 pr-12"
                      placeholder="••••••••"
                      required
                      data-testid="reset-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors">
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-velvet_text-secondary">{t('confirm')}</Label>
                  <Input
                    id="confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12"
                    placeholder="••••••••"
                    required
                    data-testid="reset-confirm-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12 font-semibold shadow-neon"
                  data-testid="reset-submit-button"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : t('submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
